import { useEffect, useState, type FormEvent } from "react";
import { formatInstant, resolveDisplayZone, type ResourceKind } from "@checklist/shared";
import { ErrorBanner, fieldCls, labelCls, SuccessBanner } from "./AuthShell.js";
import { Button } from "./ui/Button.js";
import { useConfirm } from "./ui/ConfirmDialog.js";
import { Modal } from "./ui/Modal.js";
import { humanizeApiError } from "../lib/apiErrors.js";
import { createdLinkAfterRevoke } from "../lib/shareLinkState.js";
import {
  createInvitation,
  createShareLink,
  decideAccessRequestInPanel,
  fetchAccessRequests,
  fetchInvitations,
  fetchResourceMembers,
  fetchShareLinks,
  removeResourceMember,
  revokeAllShareLinks,
  revokeInvitation,
  revokeShareLink,
  rotateShareLink,
  type ApiAccessRequest,
  type ApiChecklistRole,
  type ApiEmailInvitation,
  type ApiMember,
  type ApiShareLink,
} from "../lib/api.js";

type ExpiryChoice = "7" | "30" | "90" | "never";

function resourceLabel(kind: ResourceKind): string {
  return kind === "checklist" ? "checklist" : kind === "template" ? "template" : "list";
}

function roleLabel(role: "owner" | ApiChecklistRole): string {
  return role === "owner" ? "Owner" : role === "write" ? "Can edit" : "View only";
}

function formatDate(value?: string): string {
  if (!value) return "Never";
  const zone = resolveDisplayZone();
  const text = formatInstant(value, zone);
  return text || "Unknown";
}

function relativeTime(value: string): string {
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 60_000) return "just now";
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function expiryText(value?: string): { text: string; urgent: boolean } {
  if (!value) return { text: "Never", urgent: false };
  const remaining = new Date(value).getTime() - Date.now();
  if (!Number.isFinite(remaining)) return { text: "Unknown", urgent: false };
  const days = Math.ceil(remaining / 86_400_000);
  if (days <= 0) return { text: "Expired", urgent: true };
  return { text: `in ${days} day${days === 1 ? "" : "s"}`, urgent: days < 3 };
}

export function SharingPanel({
  resourceKind,
  resourceId,
  resourceName,
  isOwner,
  open,
  onClose,
  onChanged,
}: {
  resourceKind: ResourceKind;
  resourceId: string;
  resourceName: string;
  isOwner: boolean;
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const confirm = useConfirm();
  const [members, setMembers] = useState<ApiMember[]>([]);
  const [invitations, setInvitations] = useState<ApiEmailInvitation[]>([]);
  const [links, setLinks] = useState<ApiShareLink[]>([]);
  const [requests, setRequests] = useState<ApiAccessRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<ApiChecklistRole>("write");
  const [inviteFallbackUrl, setInviteFallbackUrl] = useState<string | null>(null);
  const [linkRole, setLinkRole] = useState<ApiChecklistRole>("read");
  const [expiry, setExpiry] = useState<ExpiryChoice>("30");
  const [label, setLabel] = useState("");
  const [requireSignIn, setRequireSignIn] = useState(false);
  const [createdLink, setCreatedLink] = useState<ApiShareLink | null>(null);
  const [activeTab, setActiveTab] = useState<"invite" | "links" | "people">("invite");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const loadedMembers = await fetchResourceMembers(resourceKind, resourceId);
      setMembers(loadedMembers);
      if (isOwner) {
        const [loadedInvitations, loadedLinks, loadedRequests] = await Promise.all([
          fetchInvitations(resourceKind, resourceId),
          fetchShareLinks(resourceKind, resourceId),
          fetchAccessRequests(resourceKind, resourceId),
        ]);
        setInvitations(loadedInvitations);
        setLinks(loadedLinks);
        setRequests(loadedRequests);
      } else {
        setInvitations([]);
        setLinks([]);
        setRequests([]);
      }
    } catch (err) {
      setError(humanizeApiError(err, "Failed to load sharing details."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setSuccess(null);
    setInviteFallbackUrl(null);
    setCreatedLink(null);
    setActiveTab("invite");
    void load();
  }, [open, resourceId, resourceKind, isOwner]);

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess("Link copied.");
    } catch {
      setError("Could not copy the link. Select it and copy manually.");
    }
  }

  async function reloadAfterChange(message: string) {
    await load();
    setSuccess(message);
    onChanged?.();
  }

  async function onRemoveMember(sub: string) {
    const ok = await confirm({
      title: `Remove this person?`,
      message: `They will lose access to this ${resourceLabel(resourceKind)}.`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    setWorking(`member:${sub}`);
    setError(null);
    try {
      await removeResourceMember(resourceKind, resourceId, sub);
      await reloadAfterChange("Member removed.");
    } catch (err) {
      setError(humanizeApiError(err, "Failed to remove member."));
    } finally {
      setWorking(null);
    }
  }

  async function onRevokeInvitation(email: string) {
    setWorking(`invite:${email}`);
    setError(null);
    try {
      await revokeInvitation(resourceKind, resourceId, email);
      await reloadAfterChange("Invitation revoked.");
    } catch (err) {
      setError(humanizeApiError(err, "Failed to revoke invitation."));
    } finally {
      setWorking(null);
    }
  }

  async function onInvite(event: FormEvent) {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setWorking("invite");
    setError(null);
    setInviteFallbackUrl(null);
    try {
      const result = await createInvitation(resourceKind, resourceId, {
        email,
        role: inviteRole,
      });
      setInviteEmail("");
      if (!result.emailDelivered) {
        setInviteFallbackUrl(result.url);
        setSuccess("We couldn't send the email - share this link directly.");
      } else {
        setSuccess("Invitation sent.");
      }
      await load();
      onChanged?.();
    } catch (err) {
      setError(humanizeApiError(err, "Failed to send invitation."));
    } finally {
      setWorking(null);
    }
  }

  async function onCreateLink() {
    setWorking("create-link");
    setError(null);
    setCreatedLink(null);
    try {
      const result = await createShareLink(resourceKind, resourceId, {
        role: linkRole,
        requireSignIn,
        ...(expiry === "never" ? {} : { expiresInDays: Number(expiry) }),
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setCreatedLink(result);
      setLabel("");
      await load();
      setSuccess("Share link created.");
      onChanged?.();
    } catch (err) {
      setError(humanizeApiError(err, "Failed to create share link."));
    } finally {
      setWorking(null);
    }
  }

  async function onRevokeLink(token: string) {
    setWorking(`link:${token}`);
    setError(null);
    try {
      await revokeShareLink(resourceKind, resourceId, token);
      setCreatedLink((prev) => createdLinkAfterRevoke(prev, token));
      await reloadAfterChange("Share link revoked.");
    } catch (err) {
      setError(humanizeApiError(err, "Failed to revoke share link."));
    } finally {
      setWorking(null);
    }
  }

  async function onRotateLink(token: string) {
    setWorking(`rotate:${token}`);
    setError(null);
    try {
      const result = await rotateShareLink(resourceKind, resourceId, token);
      setCreatedLink(result);
      await reloadAfterChange("Share link rotated. Copy the new link below.");
    } catch (err) {
      setError(humanizeApiError(err, "Failed to rotate share link."));
    } finally {
      setWorking(null);
    }
  }

  async function onRevokeAll() {
    const labelText = resourceLabel(resourceKind);
    const confirmed = await confirm({
      title: "Revoke all share links?",
      message: `This immediately cuts off everyone using any link to this ${labelText}, including printed QR codes. Members who signed in keep their access.`,
      confirmLabel: "Revoke all",
      destructive: true,
    });
    if (!confirmed) return;
    setWorking("revoke-all");
    setError(null);
    try {
      const result = await revokeAllShareLinks(resourceKind, resourceId);
      setCreatedLink((prev) => createdLinkAfterRevoke(prev, "all"));
      await reloadAfterChange(
        result.revoked === 0 ? "There were no active links to revoke." : "All active links were revoked."
      );
    } catch (err) {
      setError(humanizeApiError(err, "Failed to revoke all share links."));
    } finally {
      setWorking(null);
    }
  }

  async function onDecideRequest(
    requestId: string,
    decision: "approve" | "deny",
    role?: ApiChecklistRole
  ) {
    setWorking(`request:${requestId}:${decision}:${role ?? ""}`);
    setError(null);
    try {
      await decideAccessRequestInPanel(resourceKind, resourceId, requestId, {
        decision,
        ...(role ? { role } : {}),
      });
      await reloadAfterChange(
        decision === "deny" ? "Request declined." : `Access granted with ${roleLabel(role!)} access.`
      );
    } catch (err) {
      setError(humanizeApiError(err, "Failed to decide the request."));
    } finally {
      setWorking(null);
    }
  }

  const activeLinks = links.filter((link) => !link.revokedAt);
  const pendingRequests = requests.filter((request) => request.status === "pending");
  // Once claimed, the invitee already has a row in `members` — keep the
  // EMAILINV# record's own view collapsed into that instead of double-listing.
  const pendingInvitations = invitations.filter((invitation) => !invitation.accepted);

  const peopleSection = (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-ink">People with access</h3>
      {loading ? (
        <p className="mt-2 text-sm text-muted">Loading...</p>
      ) : members.length === 0 ? (
        <p className="mt-2 text-sm text-muted">No people have signed-in access yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {members.map((member) => (
            <li
              key={member.sub}
              className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{member.screenName || "Member"}</p>
                <p className="text-xs text-muted-sm">{roleLabel(member.role)}</p>
              </div>
              {isOwner && member.role !== "owner" ? (
                <button
                  type="button"
                  className="shrink-0 text-xs font-semibold text-danger hover:underline"
                  disabled={working === `member:${member.sub}`}
                  onClick={() => void onRemoveMember(member.sub)}
                >
                  {working === `member:${member.sub}` ? "Removing..." : "Remove"}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {isOwner && pendingInvitations.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {pendingInvitations.map((invitation) => (
            <li
              key={invitation.email}
              className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-line px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{invitation.email}</p>
                <p className="text-xs text-muted-sm">
                  Invited - hasn't signed in yet - {roleLabel(invitation.role)}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {!invitation.emailDelivered && invitation.url ? (
                  <button
                    type="button"
                    className="text-xs font-semibold text-brand-700 hover:underline"
                    onClick={() => void copy(invitation.url!)}
                  >
                    Copy link
                  </button>
                ) : null}
                <button
                  type="button"
                  className="text-xs font-semibold text-danger hover:underline"
                  disabled={working === `invite:${invitation.email}`}
                  onClick={() => void onRevokeInvitation(invitation.email)}
                >
                  {working === `invite:${invitation.email}` ? "Revoking..." : "Revoke"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );

  return (
    <Modal open={open} onClose={onClose} labelledBy="sharing-panel-title">
      <h2 id="sharing-panel-title" className="text-lg font-semibold text-ink">
        Share {resourceLabel(resourceKind)}
      </h2>
      <p className="mt-1 text-sm text-muted">{resourceName}</p>

      <div className="mt-4 space-y-2">
        <ErrorBanner message={error} />
        <SuccessBanner message={success} />
      </div>

      {!isOwner ? (
        <>
          {peopleSection}
          <p className="mt-6 text-sm text-muted">
            Only the owner can invite people or manage share links.
          </p>
        </>
      ) : (
        <>
          <div className="mt-6 flex gap-1 border-b border-line" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "invite"}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${
                activeTab === "invite"
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-muted hover:text-ink"
              }`}
              onClick={() => setActiveTab("invite")}
            >
              Invite by email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "links"}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${
                activeTab === "links"
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-muted hover:text-ink"
              }`}
              onClick={() => setActiveTab("links")}
            >
              Links
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "people"}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${
                activeTab === "people"
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-muted hover:text-ink"
              }`}
              onClick={() => setActiveTab("people")}
            >
              People
            </button>
          </div>

          {/*
            All three tab panels are stacked into the same CSS grid cell so the
            grid row sizes to the tallest one. The inactive panels stay mounted
            with `invisible` (not `hidden`/display:none) so they keep occupying
            layout space - that's what stops the modal from resizing (and the
            page from re-centering) when switching tabs. `invisible` also drops
            the panel from the accessibility/focus tree, so it behaves like
            display:none for keyboard and screen-reader users.
          */}
          <div className="grid grid-cols-1">
            <div
              className={`col-start-1 row-start-1 mt-6 ${activeTab === "invite" ? "" : "invisible"}`}
              aria-hidden={activeTab !== "invite"}
            >
              <section>
                <h3 className="text-sm font-semibold text-ink">Invite by email</h3>
                <form className="mt-3 space-y-3" onSubmit={(event) => void onInvite(event)}>
                  <div>
                    <label className={labelCls} htmlFor="sharing-invite-email">
                      Email
                    </label>
                    <input
                      id="sharing-invite-email"
                      type="email"
                      className={fieldCls}
                      placeholder="friend@example.com"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      required
                    />
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-36 flex-1">
                      <label className={labelCls} htmlFor="sharing-invite-role">
                        Role
                      </label>
                      <select
                        id="sharing-invite-role"
                        className={fieldCls}
                        value={inviteRole}
                        onChange={(event) => setInviteRole(event.target.value as ApiChecklistRole)}
                      >
                        <option value="write">Can edit</option>
                        <option value="read">View only</option>
                      </select>
                    </div>
                    <Button type="submit" disabled={working === "invite"}>
                      {working === "invite" ? "Sending..." : "Invite"}
                    </Button>
                  </div>
                </form>
                {inviteFallbackUrl ? (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                    <p>We couldn't send the email - share this link directly.</p>
                    <p className="mt-2 break-all text-xs">{inviteFallbackUrl}</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => void copy(inviteFallbackUrl)}
                    >
                      Copy link
                    </Button>
                  </div>
                ) : null}
              </section>

              {pendingRequests.length > 0 ? (
                <section className="mt-7 border-t border-line pt-6">
                  <h3 className="text-sm font-semibold text-ink">Pending requests</h3>
                  <ul className="mt-3 space-y-3">
                    {pendingRequests.map((request) => (
                      <li key={request.requestId} className="rounded-xl border border-line p-3 text-sm">
                        <p className="font-semibold text-ink">
                          {request.requesterName || request.requesterEmail}
                        </p>
                        <p className="text-muted">{request.requesterEmail} - {relativeTime(request.createdAt)}</p>
                        {request.message ? (
                          <p className="mt-2 whitespace-pre-wrap text-sm text-ink">{request.message}</p>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            disabled={working?.startsWith(`request:${request.requestId}:`) ?? false}
                            onClick={() => void onDecideRequest(request.requestId, "approve", "read")}
                          >
                            View only
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={working?.startsWith(`request:${request.requestId}:`) ?? false}
                            onClick={() => void onDecideRequest(request.requestId, "approve", "write")}
                          >
                            Can edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-danger"
                            disabled={working?.startsWith(`request:${request.requestId}:`) ?? false}
                            onClick={() => void onDecideRequest(request.requestId, "deny")}
                          >
                            Decline
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>

            <div
              className={`col-start-1 row-start-1 mt-6 ${activeTab === "links" ? "" : "invisible"}`}
              aria-hidden={activeTab !== "links"}
            >
              <section>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-ink">Links</h3>
                  <button
                    type="button"
                    className="text-xs font-semibold text-danger hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                    disabled={working === "revoke-all" || activeLinks.length === 0}
                    onClick={() => void onRevokeAll()}
                  >
                    {working === "revoke-all" ? "Revoking..." : "Revoke all links"}
                  </button>
                </div>
                <p className="mt-2 text-xs text-muted-sm">
                  Each link carries its own permission and expiry. Only people you give a link to can open this - sharing it once doesn't make it public, and revoking a link cuts off access immediately.
                </p>
                <div className="mt-4 overflow-x-auto rounded-lg border border-line">
                  <table className="w-full min-w-150 text-left text-xs">
                    <thead className="bg-slate-50 text-muted-sm">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Role</th>
                        <th className="px-3 py-2 font-semibold">Label</th>
                        <th className="px-3 py-2 font-semibold">Created</th>
                        <th className="px-3 py-2 font-semibold">Expires</th>
                        <th className="px-3 py-2 font-semibold">Uses</th>
                        <th className="px-3 py-2 font-semibold">Last used</th>
                        <th className="px-3 py-2 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeLinks.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-4 text-center text-muted">
                            No active links.
                          </td>
                        </tr>
                      ) : (
                        activeLinks.map((link) => {
                          const expiryInfo = expiryText(link.expiresAt);
                          return (
                            <tr key={link.token} className="border-t border-line text-ink">
                              <td className="px-3 py-2">{roleLabel(link.role)}</td>
                              <td className="max-w-32 truncate px-3 py-2">{link.label || "-"}</td>
                              <td className="px-3 py-2">{formatDate(link.createdAt)}</td>
                              <td className="px-3 py-2">
                                <span className={expiryInfo.urgent ? "rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-900" : ""}>
                                  {expiryInfo.text}
                                </span>
                              </td>
                              <td className="px-3 py-2">{link.useCount}</td>
                              <td className="px-3 py-2">{link.lastUsedAt ? relativeTime(link.lastUsedAt) : "Never"}</td>
                              <td className="whitespace-nowrap px-3 py-2">
                                <button
                                  type="button"
                                  className="font-semibold text-brand-700 hover:underline"
                                  disabled={working === `rotate:${link.token}`}
                                  onClick={() => void onRotateLink(link.token)}
                                >
                                  Rotate
                                </button>
                                <span className="px-1 text-muted-sm">/</span>
                                <button
                                  type="button"
                                  className="font-semibold text-danger hover:underline"
                                  disabled={working === `link:${link.token}`}
                                  onClick={() => void onRevokeLink(link.token)}
                                >
                                  Revoke
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 rounded-xl border border-line bg-slate-50 p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="sharing-link-role">Role</label>
                      <select
                        id="sharing-link-role"
                        className={fieldCls}
                        value={linkRole}
                        onChange={(event) => setLinkRole(event.target.value as ApiChecklistRole)}
                      >
                        <option value="read">View only</option>
                        <option value="write">Can edit</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="sharing-link-expiry">Expires</label>
                      <select
                        id="sharing-link-expiry"
                        className={fieldCls}
                        value={expiry}
                        onChange={(event) => setExpiry(event.target.value as ExpiryChoice)}
                      >
                        <option value="7">7 days</option>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                        <option value="never">Never</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className={labelCls} htmlFor="sharing-link-label">
                      Label <span className="text-muted-sm">(optional)</span>
                    </label>
                    <input
                      id="sharing-link-label"
                      className={fieldCls}
                      maxLength={60}
                      value={label}
                      onChange={(event) => setLabel(event.target.value)}
                      placeholder="e.g. Team printout"
                    />
                  </div>
                  <label className="mt-3 flex items-start gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={requireSignIn}
                      onChange={(event) => setRequireSignIn(event.target.checked)}
                    />
                    <span>
                      Require sign-in
                      <span className="mt-0.5 block text-xs text-muted-sm">
                        Recipients must sign in - lets you see who did what and remove them individually.
                      </span>
                    </span>
                  </label>
                  {linkRole === "write" && !requireSignIn ? (
                    <p className="mt-3 text-xs font-medium text-amber-900">
                      Anyone with this link can edit without signing in.
                    </p>
                  ) : null}
                  <Button className="mt-4" disabled={working === "create-link"} onClick={() => void onCreateLink()}>
                    {working === "create-link" ? "Creating..." : "Create link"}
                  </Button>
                </div>
                {createdLink ? (
                  <div className="mt-3 rounded-lg border border-line bg-slate-50 p-3">
                    <p className="break-all text-xs text-ink">{createdLink.url}</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => void copy(createdLink.url)}
                    >
                      Copy link
                    </Button>
                  </div>
                ) : null}
              </section>
            </div>

            <div
              className={`col-start-1 row-start-1 ${activeTab === "people" ? "" : "invisible"}`}
              aria-hidden={activeTab !== "people"}
            >
              {peopleSection}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
